/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isExpandable } from '../utils/common';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import Copier from './Copier';
import { ChevronDown16 } from '@carbon/icons-react';

const ExpandableDisplay = ({ copyableContent, expanded, onExpand, expandLinesAfter = 8, children }) => {
  const [expandedState, updateExpandedState] = useState(expanded ? expanded : false);
  const [expandable, updateExpandable] = useState(false);

  const { t } = useTranslation();
  const copyRef = useRef(`copyable_content_${uniqueId()}`);

  useEffect(() => {
    updateExpandedState(expanded);
  }, [expanded]);

  useEffect(() => {
    const elementNode = children ? document.querySelector(`.${copyRef.current}`) : null;
    updateExpandable(isExpandable(elementNode ? elementNode.textContent : null, expandLinesAfter));
  }, [expandLinesAfter, copyableContent, children]);

  if (!onExpand) {
    onExpand = () => {
      updateExpandedState(!expandedState);
    };
  }

  const moreLessButton = (
    <button
      className={classNames(`bx--btn`, `bx--btn--ghost`, `bx--btn--field`, `bx--snippet-btn--expand`)}
      type="button"
      onClick={onExpand}
      data-testid={'more-less-button'}
    >
      <span className={`bx--snippet-btn--text`}>{expandedState ? t('show_less') : t('show_more')}</span>
      <ChevronDown16
        aria-label={expandedState ? t('show_less') : t('show_more')}
        className={`bx--icon-chevron--down bx--snippet__icon`}
        name="chevron--down"
        role="img"
      />
    </button>
  );

  return (
    <div className="expandableDisplay" data-testid={'expandableDisplay'}>
      <div
        className={classNames('bx--snippet', 'bx--snippet--multi', 'bx--snippet--wraptext', { 'bx--snippet--expand': expandedState })}
        data-testid={'content_panel'}
      >
        <div
          className={classNames('bx--snippet-container', 'body', { expanded: expandedState }, { small: !expandable })}
          data-testid={'expandable-display'}
          tabIndex={'0'}
        >
          <div className={'boundedSection ' + (!copyableContent && copyRef.current)} data-testid={'default-copyable-content'}>
            {children}
          </div>
        </div>
        {copyableContent && (
          <div className={classNames('hiddenCopy', copyRef.current)} data-testid={'copyable-content'}>
            <pre>
              <code>{copyableContent}</code>
            </pre>
          </div>
        )}
        <Copier element={`.${copyRef.current}`} />
        {expandable && moreLessButton}
      </div>
    </div>
  );
};

ExpandableDisplay.propTypes = {
  copyableContent: PropTypes.string, // If you want a specific copyable content
  expanded: PropTypes.bool, // If you want to control the expanded state
  onExpand: PropTypes.func, // If you want to control the expanded state
  expandLinesAfter: PropTypes.number, // If you want to set a custom numbers of lines to trigger the expand button (Defaults to 8)
  children: PropTypes.node // If you want additional elements displayed under the JSON Viewer
};

export default ExpandableDisplay;
